// src/utils/api.js
import { getApiUrl, AMPLIFY_API_URL } from '../config/api-config';
import { get, post, put, del } from 'aws-amplify/api';
import awsconfig from '../aws-exports';

// Get the Amplify API name from config
const AMPLIFY_API_NAME = awsconfig.aws_cloud_logic_custom?.[0]?.name || 'smackApi';

// Utility function to get the current auth token (for SAM API calls)
export const getAuthToken = async () => {
  // Try to get token from sessionStorage (saved during login)
  const token = sessionStorage.getItem('authToken');
  if (token) return token;

  // If no token in storage, try to get from Amplify (for backward compatibility)
  try {
    const { fetchAuthSession } = await import('aws-amplify/auth');
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    if (idToken) {
      // Save token for future requests
      sessionStorage.setItem('authToken', idToken);
      return idToken;
    }
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }

  return null;
};

// Check if endpoint uses Amplify API
const isAmplifyEndpoint = (endpoint) => {
  // Remove query string to check the base path
  const basePath = endpoint.split('?')[0];
  return getApiUrl(basePath) === AMPLIFY_API_URL;
};

// Base API call function with auth
export const apiCall = async (endpoint, method = 'GET', body = null, queryParams = null) => {
  const apiBaseUrl = getApiUrl(endpoint);

  // Use Amplify API client for Amplify endpoints
  if (isAmplifyEndpoint(endpoint)) {
    // Parse endpoint and query params
    const [basePath, queryString] = endpoint.split('?');

    // Combine inline query params with passed queryParams
    const allQueryParams = { ...queryParams };
    if (queryString) {
      const urlParams = new URLSearchParams(queryString);
      urlParams.forEach((value, key) => {
        allQueryParams[key] = value;
      });
    }

    const options = {
      apiName: AMPLIFY_API_NAME,
      path: basePath,
      options: {
        ...(body ? { body } : {}),
        ...(Object.keys(allQueryParams).length > 0 ? { queryParams: allQueryParams } : {})
      }
    };

    try {
      let response;
      switch (method) {
        case 'GET':
          response = await get(options).response;
          break;
        case 'POST':
          response = await post(options).response;
          break;
        case 'PUT':
          response = await put(options).response;
          break;
        case 'DELETE':
          response = await del(options).response;
          break;
        default:
          throw new Error(`Unsupported method: ${method}`);
      }

      const data = await response.body.json();
      return data;
    } catch (error) {
      console.error('Amplify API error:', error);
      throw error;
    }
  }

  // Use raw fetch for SAM API endpoints
  const token = await getAuthToken();

  if (!token) {
    throw new Error('No authentication token found');
  }

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': token
  };

  const config = {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined
  };

  const response = await fetch(`${apiBaseUrl}${endpoint}`, config);

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API error: ${response.status} - ${errorText}`);
  }

  return response.json();
};

// GET request
export const apiGet = async (endpoint, queryParams = {}) => {
  // For Amplify endpoints, pass query params separately
  if (isAmplifyEndpoint(endpoint)) {
    return apiCall(endpoint, 'GET', null, queryParams);
  }

  // For SAM endpoints, append query string to URL
  const queryString = Object.keys(queryParams).length > 0
    ? '?' + new URLSearchParams(queryParams).toString()
    : '';

  return apiCall(`${endpoint}${queryString}`);
};

// POST request
export const apiPost = async (endpoint, data = {}) => {
  return apiCall(endpoint, 'POST', data);
};

// PUT request
export const apiPut = async (endpoint, data = {}) => {
  return apiCall(endpoint, 'PUT', data);
};

// DELETE request
export const apiDelete = async (endpoint) => {
  return apiCall(endpoint, 'DELETE');
};